import React, { useState, useEffect, Fragment } from 'react';
import { Typography, Grid, Button, TextField, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, InputLabel } from '@material-ui/core';
import Select from 'react-select';
import APIService from '../services/APIService';
import PaddedGrid from '../components/PaddedGrid';
import AppContext from '../AppContext';
import GetAppIcon from '@material-ui/icons/GetApp';
import { useTranslation } from 'react-i18next';
import { municipalities } from '../constants/municipalities';
import { regions } from '../constants/regions';

const customStyles = {
    control: (provided) => ({
        ...provided,
        fontFamily: "Roboto, sans-serif",
    }),
    menu: (provided) => ({
        ...provided,
        fontFamily: "Roboto, sans-serif",
    }),
    option: (provided) => ({
        ...provided,
        fontFamily: "Roboto, sans-serif",
    }),
};

const Overview = () => {
    const { t, i18n } = useTranslation();
    const [selectedRestaurants, setSelectedRestaurants] = useState([]);
    const [restaurants, setRestaurants] = useState([]);
    const [visibleRestaurants, setVisibleRestaurants] = useState([]);
    const [selectedFoodServiceCategories, setSelectedFoodServiceCategories] = useState([]);
    const [foodServiceCategories, setFoodServiceCategories] = useState([]);
    const [selectedMunicipalities, setSelectedMunicipalities] = useState([]);
    const [municipalitiesList, setMunicipalitiesList] = useState(municipalities);
    const [selectedRegions, setSelectedRegions] = useState([]);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [files, setFiles] = useState([]);

    const refreshExports = () => {
        APIService.getExports()
            .then(exportData => {
                let isGenerating = exportData.filter((file) => ['new', 'processing'].includes(file.status)).length > 0;
                if (isGenerating) {
                    setTimeout(() => {
                        refreshExports();
                    }, 10000);
                }
                setFiles(exportData);
            })
            .catch(error => {
                console.error('Error fetching exports:', error);
            });
    };

    const exportData = () => {
        APIService.export(
            selectedRestaurants,
            selectedFoodServiceCategories,
            selectedMunicipalities,
            selectedRegions,
            from,
            to,
            i18n.language || 'fi',
        )
            .then(refreshExports)
            .catch(error => {
                console.error('Error exporting data:', error);
            });
    };

    useEffect(() => {
        APIService.getRestaurants().then((fetchedRestaurants) => {
            fetchedRestaurants.sort((a, b) => a.name > b.name ? 1 : -1);
            setRestaurants(fetchedRestaurants);
            setVisibleRestaurants(fetchedRestaurants);
        });

        APIService.getFoodServiceCategories().then((fetchedCategories) => {
            setFoodServiceCategories(fetchedCategories);
        });

        refreshExports();
    }, []);

    // When selected regions change
    useEffect(() => {
        const filteredMunicipalities = selectedRegions.length > 0 ? municipalities.filter((municipality) =>
            selectedRegions.includes(municipality.regionId)
        ) : municipalities;

        const filteredSelectedMunicipalities = selectedMunicipalities.filter((municipalityId) =>
            filteredMunicipalities.some(municipality => municipality.value === municipalityId)
        );

        setMunicipalitiesList(filteredMunicipalities);
        setSelectedMunicipalities(filteredSelectedMunicipalities);
    }, [selectedRegions]);

    // When selected municipalities change
    useEffect(() => {
        const filteredMunicipalities = selectedMunicipalities.length > 0 ? municipalitiesList.filter((municipality) =>
            selectedMunicipalities.includes(municipality.value)
        ) : municipalitiesList;

        const filteredRestaurants = restaurants.filter((restaurant) =>
            filteredMunicipalities.some(municipality => municipality.label === restaurant.municipality)
        );

        const filteredSelectedRestaurants = selectedRestaurants.filter((restaurantId) =>
            filteredRestaurants.some(restaurant => restaurant.id === restaurantId)
        );

        setVisibleRestaurants(filteredRestaurants);
        setSelectedRestaurants(filteredSelectedRestaurants);
    }, [selectedMunicipalities]);

    return (
        <div style={{ maxWidth: '800px' }}>
            <form method="POST" action="/export/reports">
                <PaddedGrid>
                    <Grid item xs={12}>
                        <Typography variant='title'>{t('Raportti')}</Typography>
                    </Grid>

                    {!AppContext.isUser() && (
                        <Fragment>
                            <Grid item xs={12}>
                                <Grid style={{ width: '100%' }}>
                                    <InputLabel shrink style={{ fontSize: '12px' }}>{t('Maakunta')}</InputLabel>
                                    <Select
                                        options={regions}
                                        isMulti
                                        isClearable
                                        closeMenuOnSelect={false}
                                        styles={customStyles}
                                        placeholder=""
                                        onChange={(selectedOptions) => setSelectedRegions(selectedOptions ? selectedOptions.map(option => option.value) : [])}
                                        value={regions.filter(region => selectedRegions.includes(region.value))}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid style={{ width: '100%' }}>
                                    <InputLabel shrink style={{ fontSize: '12px' }}>{t('Kunta')}</InputLabel>
                                    <Select
                                        options={municipalitiesList}
                                        isMulti
                                        isClearable
                                        closeMenuOnSelect={false}
                                        styles={customStyles}
                                        placeholder=""
                                        onChange={(selectedOptions) => setSelectedMunicipalities(selectedOptions ? selectedOptions.map(option => option.value) : [])}
                                        value={municipalitiesList.filter(municipality => selectedMunicipalities.includes(municipality.value))}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid style={{ width: '100%' }}>
                                    <InputLabel shrink style={{ fontSize: '12px' }}>{t('Ravintola')}</InputLabel>
                                    <Select
                                        options={visibleRestaurants.map(r => ({ value: r.id, label: r.name }))}
                                        isMulti
                                        isClearable
                                        closeMenuOnSelect={false}
                                        styles={customStyles}
                                        placeholder=""
                                        onChange={(selectedOptions) => setSelectedRestaurants(selectedOptions ? selectedOptions.map(option => option.value) : [])}
                                        value={visibleRestaurants.filter(r => selectedRestaurants.includes(r.id)).map(r => ({ value: r.id, label: r.name }))}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid style={{ width: '100%' }}>
                                    <InputLabel shrink style={{ fontSize: '12px' }}>{t('Ravintolatyyppi')}</InputLabel>
                                    <Select
                                        options={foodServiceCategories.map(c => ({ value: c.id, label: c.name }))}
                                        isMulti
                                        isClearable
                                        closeMenuOnSelect={false}
                                        styles={customStyles}
                                        placeholder=""
                                        onChange={(selectedOptions) => setSelectedFoodServiceCategories(selectedOptions ? selectedOptions.map(option => option.value) : [])}
                                        value={foodServiceCategories.filter(c => selectedFoodServiceCategories.includes(c.id)).map(c => ({ value: c.id, label: c.name }))}
                                    />
                                </Grid>
                            </Grid>
                        </Fragment>
                    )}

                    <Grid item xs={6}>
                        <InputLabel shrink style={{ fontSize: '12px' }}>{t('Päivämäärästä')}</InputLabel>
                        <TextField
                            fullWidth
                            type="date"
                            onChange={(e) => setFrom(e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <InputLabel shrink style={{ fontSize: '12px' }}>{t('Päivämäärään')}</InputLabel>
                        <TextField
                            fullWidth
                            type="date"
                            onChange={(e) => setTo(e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Button variant="raised" color="primary" onClick={exportData}>{t('Vie kirjaukset')}</Button>
                    </Grid>
                </PaddedGrid>
            </form>

            <PaddedGrid>
                <Grid item xs={12}>
                    <Typography variant="title">{t('Aikaisemmat raportit')}</Typography>
                    <Typography>{t('Viedyt raportit näkyvät tässä 24 tuntia valmistumisen jälkeen')}</Typography>
                </Grid>

                <Grid item xs={12}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('Nimi')}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {files.map((file, index) =>
                                <TableRow key={index}>
                                    <TableCell>{file.filename}</TableCell>

                                    <TableCell style={{ textAlign: 'right' }}>
                                        {['new', 'processing'].includes(file.status) && <Typography style={{ display: 'flex', alignItems: 'center', justifyContent: 'right' }}><CircularProgress style={{ width: '25px', height: '25px', marginRight: '10px' }} /> {t('Raporttia valmistetaan')}</Typography>}
                                        {file.status === 'done' && <a href={file.url} className="Overview-DownloadButton" target="_blank" rel="noopener noreferrer" download>{t('Lataa tästä')} <GetAppIcon /></a>}
                                        {file.status === 'failed' && <Typography>{t('Epäonnistui')}</Typography>}
                                    </TableCell>
                                </TableRow>)
                            }
                        </TableBody>
                    </Table>
                </Grid>
            </PaddedGrid>
        </div>
    );
};

export default Overview;



// WEBPACK FOOTER //
// ./resources/assets/js/containers/Overview.js